/**
 * Ball Physics Model — COR, compression, and cover interaction physics.
 *
 * Models three real phenomena that the old system treated as tiny constants:
 *
 *   1. COR(v, compression): Coefficient of Restitution varies with impact velocity
 *      AND ball compression. Soft balls are more efficient at low speeds; firm balls
 *      recover their advantage at high speeds. The crossover is ~85-95 mph.
 *
 *   2. Cover friction spin: Urethane generates significantly more spin than ionomer/surlyn,
 *      especially on wedge shots where contact time is longer and loft is higher.
 *      The effect scales with dynamic loft — a driver with 10° loft sees much less
 *      cover-spin difference than a 50° wedge.
 *
 *   3. Ball deformation launch: Softer balls compress more at impact, effectively adding
 *      dynamic loft. The effect is small but real — ~0.2-0.5° for the softest balls.
 *
 * References:
 *   - Penner (2003): "The physics of golf" — COR vs impact speed model
 *   - Cochran & Stobbs: "Search for the Perfect Swing" — contact mechanics
 *   - USGA/R&A COR testing protocol: max COR 0.830 at 143 ft/s (98 mph)
 *   - Titleist ball fitting data: compression vs swing speed recommendations
 *   - MyGolfSpy ball test data: spin separation by cover type and club
 */

import type { BallCover } from './types';

// ── COR Model Constants ────────────────────────────────────────────────────

/** Maximum conforming COR (USGA limit). */
const COR_MAX = 0.830;

/** USGA test speed for COR rating (ft/s → converted to mph for our system). */
const COR_TEST_SPEED_MPH = 98;  // 143 ft/s ≈ 98 mph

/**
 * COR reference at standard compression (90) and test speed.
 * Premium tour balls achieve ~0.825-0.830. Distance balls ~0.810-0.820.
 */
const COR_REF = 0.825;

/**
 * Ball compression where COR is maximized at the test speed.
 * This is the "sweet spot" compression for a ~100 mph swing.
 */
const COMPRESSION_SWEET_SPOT = 90;

// ── Cover Friction Constants ───────────────────────────────────────────────

/**
 * Base spin multiplier by cover type, relative to urethane at 1.0.
 * These represent the friction coefficient difference during impact.
 *
 * Urethane: soft, high-friction — grabs grooves, generates max spin.
 * Surlyn: harder, moderate friction — ~8-12% less spin on short irons/wedges.
 * Ionomer: hardest, low friction — ~15-20% less spin on wedges.
 *
 * The difference is loft-dependent: at low loft (driver), all covers behave
 * similarly because the ball compresses against the face rather than sliding
 * across grooves. At high loft (wedge), groove interaction dominates.
 */
const COVER_BASE_SPIN: Record<BallCover, number> = {
  urethane: 1.0,
  surlyn:   0.92,
  ionomer:  0.84,
};

/**
 * Maximum additional spin reduction at high loft (wedges).
 * At low loft (driver), the cover difference is minimal because
 * the ball compresses head-on rather than sliding across grooves.
 */
const COVER_WEDGE_PENALTY: Record<BallCover, number> = {
  urethane: 0.0,    // no penalty — urethane is the reference
  surlyn:   -0.06,  // additional 6% spin loss on wedges
  ionomer:  -0.10,  // additional 10% spin loss on wedges
};

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Compute COR-based ball speed modifier from club speed and compression.
 *
 * Physics model:
 *   COR depends on how well the ball's compression matches the impact force.
 *   - At low speed (<80 mph): soft balls compress more fully → higher COR.
 *     Firm balls don't fully compress → energy lost to vibration → lower COR.
 *   - At high speed (>110 mph): firm balls resist over-compression → higher COR.
 *     Soft balls over-compress → energy lost to hysteresis → lower COR.
 *   - At matched speed: COR is maximized for that compression level.
 *
 * The model uses a parabolic COR curve centered on the "ideal" club speed
 * for each compression level. Higher compression shifts the ideal speed up;
 * lower compression shifts it down.
 *
 * Real-world effect size: ~1-3% ball speed variation across the full
 * compression range (30-110) at typical swing speeds. A severe mismatch
 * (compression 40 at 120 mph) costs ~2-3 mph ball speed.
 *
 * Returns a ball speed multiplier relative to baseline (1.0 at matched).
 *
 * @param clubSpeedMph - Club head speed at impact in mph
 * @param compression - Ball compression (30-110 scale)
 */
export function computeCORSpeedMod(clubSpeedMph: number, compression: number): number {
  // 1. Find the "ideal" club speed where this compression achieves peak COR.
  //    Compression 90 is optimized for ~100 mph club speed (standard test).
  //    Every 10 compression points shifts the ideal speed by ~5 mph.
  const idealClubSpeed = COR_TEST_SPEED_MPH + (compression - COMPRESSION_SWEET_SPOT) * 0.5;

  // 2. COR drops as actual speed deviates from ideal.
  //    The sensitivity is very gentle — even a 30 mph mismatch only costs ~2%.
  //    This matches published data: Titleist recommends comp 60-80 for <90 mph
  //    and comp 90-100 for >100 mph, but the performance cliff is gradual.
  const speedDeviation = clubSpeedMph - idealClubSpeed;

  // Quadratic drop: 0.000008 per mph² of deviation
  // At 20 mph off-ideal: 400 × 0.000008 = 0.0032 (0.3% COR loss)
  // At 30 mph off-ideal: 900 × 0.000008 = 0.0072 (0.7% COR loss → ~1.2 mph on driver)
  // At 40 mph off-ideal: 1600 × 0.000008 = 0.0128 (1.3% COR loss → ~2.1 mph on driver)
  const corLoss = 0.000008 * speedDeviation * speedDeviation;

  // COR for this combination
  const cor = COR_MAX - corLoss;

  // Floor at 0.80 (even the worst mismatch doesn't drop below ~96% of max)
  const clampedCOR = Math.max(0.80, cor);

  // 3. Convert to ball speed multiplier relative to reference COR.
  //    At ideal match: COR = 0.830, mult = 0.830/0.825 ≈ 1.006 (slight boost)
  //    At 30 mph mismatch: COR ≈ 0.823, mult ≈ 0.998 (slight penalty)
  return clampedCOR / COR_REF;
}

/**
 * Compute cover-type spin modifier accounting for loft-dependent groove interaction.
 *
 * Physics: At low dynamic loft (driver, ~10°), the ball compresses into the face
 * head-on. Cover hardness barely matters — spin is mostly from the oblique
 * component of force. At high loft (wedge, ~50°), the ball slides across the
 * grooves and cover friction dominates spin generation.
 *
 * The model interpolates between "no cover effect" at 0° and "full cover effect"
 * at 50°+ using the estimated dynamic loft.
 *
 * @param cover - Ball cover material
 * @param clubKey - Club identifier for estimating dynamic loft
 * @returns Spin multiplier (1.0 = urethane baseline)
 */
export function computeCoverSpinMod(cover: BallCover, clubKey: string): number {
  if (cover === 'urethane') return 1.0;  // reference — no adjustment needed

  // Estimate effective dynamic loft by club type.
  // This determines how much the cover material matters for spin.
  const loftEstimate = estimateDynamicLoft(clubKey);

  // Loft blend: 0.0 at 0° loft (no cover effect) → 1.0 at 50°+ (full effect)
  // Uses a smooth S-curve (sigmoid-ish) that ramps up through mid-irons
  const loftFactor = Math.min(1.0, Math.max(0, (loftEstimate - 8) / 42));

  // Base spin from cover type (driver-like behavior)
  const baseSpin = COVER_BASE_SPIN[cover];

  // Additional wedge penalty blended by loft
  const wedgePenalty = COVER_WEDGE_PENALTY[cover] * loftFactor;

  // Final: at driver loft, surlyn ≈ 0.97× (small effect).
  // At wedge loft, surlyn ≈ 0.86× (large effect).
  const driverBlend = 1.0 - loftFactor;  // how much to weight the "no effect" case
  const wedgeBlend = loftFactor;

  return driverBlend * 1.0 + wedgeBlend * (baseSpin + wedgePenalty);
}

/**
 * Compute ball deformation launch angle delta.
 *
 * Physics: Softer balls deform more at impact, effectively wrapping around
 * the clubface. This increases the "effective loft" at separation — the ball
 * launches slightly higher than the clubface angle alone would produce.
 *
 * The effect is:
 *   - Largest for soft balls (compression 30-60): up to +0.5°
 *   - Neutral for standard compression (90): 0°
 *   - Slightly negative for very firm balls (100+): -0.1° (ball doesn't deform enough)
 *   - Larger at higher impact speeds (more deformation)
 *   - Larger at lower loft (ball wraps more on flat faces)
 *
 * @param compression - Ball compression (30-110)
 * @param ballSpeedMph - Ball speed in mph
 * @returns Launch angle delta in degrees
 */
export function computeDeformationLaunchDelta(
  compression: number,
  ballSpeedMph: number,
): number {
  // How much softer/firmer than neutral (90)
  const softness = (90 - compression) / 60;  // +1.0 for comp=30, 0 for 90, -0.33 for 110

  // Speed factor: more deformation at higher speeds (more force = more wrap)
  // Normalized around 130 mph ball speed (typical driver)
  const speedFactor = Math.min(1.3, Math.max(0.5, ballSpeedMph / 130));

  // Base effect: ±0.4° at extreme compression values
  const baseDelta = softness * 0.4 * speedFactor;

  // Clamp to realistic range
  return Math.max(-0.2, Math.min(0.5, baseDelta));
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Estimate dynamic loft (degrees) by club key for cover spin calculations. */
function estimateDynamicLoft(clubKey: string): number {
  const lofts: Record<string, number> = {
    driver: 12,   // 10.5° static + ~1.5° dynamic loft from attack angle
    '3wood': 16,
    '5iron': 28,
    '7iron': 34,
    '9iron': 42,
    pw: 47,
    sw: 56,
  };
  return lofts[clubKey] ?? 30;  // default to mid-iron
}
